import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { format, subDays } from 'date-fns';
import {
  AlertCircle,
  Calendar as CalendarIcon,
  FileText,
  Inbox,
  Search,
} from 'lucide-react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import TablePagination from 'src/components/common/TablePagination';
import CandidatePreviewModal from 'src/components/Modals/CandidatePreviewModal';
import DataTable from 'src/components/ui/data-table';
import { Calendar } from 'src/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { cn } from 'src/lib/utils';
import { useResumeTrackerJobCandidates } from 'src/services/api/queries/resumeTrackerQueries';
import { STATUS_META } from './statusMeta';

const API_DATE_FORMAT = 'yyyy-MM-dd';
const DISPLAY_DATE_FORMAT = 'dd MMM yyyy';
const DEFAULT_PAGE_SIZE = 10;

const PRESETS = [
  { key: 'last30', label: 'Last 30 days', days: 30 },
  { key: 'last60', label: 'Last 60 days', days: 60 },
];

const SEG_BASE =
  'inline-flex items-center gap-[6px] px-3 py-[5px] rounded-md text-[12px] font-[550] cursor-pointer transition-all whitespace-nowrap font-[inherit]';
const SEG_ACTIVE = 'bg-card text-primary shadow-[0_1px_2px_rgba(0,0,0,0.08)]';
const SEG_INACTIVE = 'text-muted-foreground hover:text-foreground';

const TAB_BASE =
  'inline-flex items-center gap-[7px] px-[18px] py-[10px] text-[13px] font-medium border-b-2 -mb-px transition-colors cursor-pointer bg-transparent border-x-0 border-t-0 whitespace-nowrap font-[inherit]';
const TAB_ACTIVE = 'text-primary font-semibold border-primary';
const TAB_INACTIVE =
  'text-muted-foreground border-transparent hover:text-foreground';

const DOT_COLORS = {
  STARTED: 'bg-muted-foreground',
  COMPLETED: 'bg-ds-success',
  FAILED: 'bg-ds-danger',
  DUPLICATE: 'bg-ds-warning',
};

const SOURCE_DISPLAY = {
  linkedin: { logo: 'in', color: '#0A66C2', name: 'LinkedIn' },
  naukri: { logo: 'N', color: '#0E7C7B', name: 'Naukri' },
  ahedge: { logo: 'A', color: '#5cd6eb', name: 'Ahedge' },
};

const PlatPill = ({ source }) => {
  const key = String(source).toLowerCase();
  const meta = SOURCE_DISPLAY[key] ?? {
    logo: key.slice(0, 2).toUpperCase(),
    color: '#64748b',
    name: source,
  };
  return (
    <span className="inline-flex items-center gap-[7px]">
      <span
        className="w-[22px] h-[22px] min-w-[22px] rounded-[6px] flex items-center justify-center text-[10.5px] font-bold text-white"
        style={{ background: meta.color }}
      >
        {meta.logo}
      </span>
      <span className="text-[12.5px] font-[500] text-muted-foreground">
        {meta.name}
      </span>
    </span>
  );
};

const fmtDuration = (seconds) => {
  if (seconds == null) return '—';
  const s = Number(seconds);
  if (s < 60) return `${s.toFixed(1)}s`;
  if (s < 3600) {
    const m = Math.floor(s / 60);
    const rem = Math.round(s % 60);
    return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const MetaSep = () => (
  <span className="w-[3px] h-[3px] rounded-full bg-borderColorSecondary inline-block shrink-0" />
);

const JobDetail = () => {
  const { jobRef } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [mode, setMode] = useState(location.state?.dateMode ?? 'last30');
  const [range, setRange] = useState({
    from: location.state?.dateFrom
      ? new Date(location.state.dateFrom)
      : subDays(new Date(), 30),
    to: location.state?.dateTo ? new Date(location.state.dateTo) : new Date(),
  });
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [previewCandidate, setPreviewCandidate] = useState(null);
  const [errorDialog, setErrorDialog] = useState(null);

  const fromStr = range?.from ? format(range.from, API_DATE_FORMAT) : null;
  const toStr = range?.to ? format(range.to, API_DATE_FORMAT) : null;

  const customLabel =
    mode === 'custom' && range?.from && range?.to
      ? `${format(range.from, DISPLAY_DATE_FORMAT)} – ${format(range.to, DISPLAY_DATE_FORMAT)}`
      : 'Custom';

  const handlePreset = (preset) => {
    setMode(preset.key);
    setRange({ from: subDays(new Date(), preset.days), to: new Date() });
  };

  const handleRangeSelect = (selected) => {
    setRange(selected);
    setMode('custom');
  };

  const currentStatus = (searchParams.get('status') || 'STARTED').toUpperCase();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPageSize = parseInt(
    searchParams.get('pageSize') || DEFAULT_PAGE_SIZE.toString(),
    10
  );
  const searchQuery = searchParams.get('q') || '';

  const { data: result, isLoading } = useResumeTrackerJobCandidates(jobRef, {
    dateFrom: fromStr,
    dateTo: toStr,
    status: currentStatus,
    page: currentPage,
    pageSize: currentPageSize,
  });

  const jobData = result?.data ?? null;
  const jobName = jobData?.job_name ?? location.state?.job?.job_name ?? jobRef;
  const summary = jobData?.summary ?? {};
  const sourceTotals = summary.source_totals ?? {};

  // Keep last known counts so tab badges don't flash 0 while switching tabs
  const stableCountsRef = useRef({});
  if (!isLoading && jobData !== null) {
    stableCountsRef.current = summary.status_counts ?? {};
  }
  const statusCounts = stableCountsRef.current;

  const totalCandidates = useMemo(
    () => Object.values(statusCounts).reduce((a, v) => a + (v ?? 0), 0),
    [statusCounts]
  );

  const updateParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    setSearchParams(next);
  };

  const handleStatusChange = (status) => updateParams({ status, page: 1 });

  const dataLoaded = !isLoading && jobData !== null;

  useEffect(() => {
    if (
      dataLoaded &&
      currentStatus === 'STARTED' &&
      (statusCounts.STARTED ?? 0) === 0
    ) {
      handleStatusChange('COMPLETED');
    }
  }, [dataLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const allCandidates = jobData?.candidates ?? [];
  const serverPagination = jobData?.pagination ?? null;

  const candidates = useMemo(() => {
    if (!searchQuery.trim()) return allCandidates;
    const q = searchQuery.toLowerCase();
    return allCandidates.filter(
      (c) =>
        c.candidate_name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q)
    );
  }, [allCandidates, searchQuery]);
  const handleSearchChange = (e) =>
    updateParams({ q: e.target.value, page: 1 });
  const handlePageChange = (page) => updateParams({ page });
  const handlePageSizeChange = (size) =>
    updateParams({ pageSize: size, page: 1 });

  const columns = useMemo(() => {
    const base = [
      {
        key: 'candidate_name',
        header: 'Name',
        sortable: true,
        width: '20%',
        className: 'text-left',
        render: (_v, row) => (
          <span className="text-[13px] font-semibold text-foreground-strong">
            {row.candidate_name || '—'}
          </span>
        ),
      },
      {
        key: 'email',
        header: 'Email',
        tooltip: true,
        width: '26%',
        className: 'text-left overflow-hidden',
        render: (_v, row) => (
          <span className="block truncate text-[12.5px] text-muted-foreground">
            {row.email || '—'}
          </span>
        ),
      },
      {
        key: 'phone',
        header: 'Phone',
        width: '15%',
        className: 'text-left',
        render: (_v, row) => (
          <span className="text-[12.5px] text-muted-foreground tabular-nums">
            {row.phone || '—'}
          </span>
        ),
      },
      {
        key: 'created_at',
        header: 'Uploaded on',
        width: '15%',
        className: 'text-left',
        render: (_v, row) => {
          if (!row.created_at)
            return (
              <span className="text-[12.5px] text-muted-foreground">—</span>
            );
          const d = new Date(row.created_at);
          return (
            <div className="flex flex-col gap-[1px]">
              <span className="text-[12.5px] text-muted-foreground tabular-nums">
                {format(d, 'dd MMM yyyy')}
              </span>
              <span className="text-[11px] text-foreground-muted tabular-nums">
                {format(d, 'hh:mm:ss a')}
              </span>
            </div>
          );
        },
      },
    ];

    const extractionCol = {
      key: 'extraction_time_seconds',
      header: 'Extraction Time',
      width: '13%',
      align: 'center',
      className: 'text-center',
      render: (_v, row) => (
        <span className="font-mono text-[12px] text-muted-foreground tabular-nums">
          {fmtDuration(row.extraction_time_seconds)}
        </span>
      ),
    };

    const remarksCol = {
      key: 'error',
      header: 'Remarks',
      width: '22%',
      className: 'text-left',
      render: (_v, row) =>
        row.error ? (
          <button
            type="button"
            data-prevent-row-click="true"
            onClick={(e) => {
              e.stopPropagation();
              setErrorDialog({ error: row.error, name: row.candidate_name });
            }}
            className="inline-flex items-center gap-[5px] text-[12px] font-[500] text-ds-danger px-[9px] py-[4px] rounded-[6px] border border-ds-danger/25 hover:bg-ds-danger/10 transition-colors whitespace-nowrap"
          >
            <AlertCircle
              className="w-[12px] h-[12px] shrink-0"
              strokeWidth={2}
            />
            View error
          </button>
        ) : (
          <span className="text-[12.5px] text-muted-foreground">—</span>
        ),
    };

    const resumeCol = {
      key: 'resume_url',
      header: 'Resume',
      width: '11%',
      align: 'center',
      className: 'text-center pr-5',
      render: (_v, row) =>
        row.resume_url ? (
          <button
            type="button"
            data-prevent-row-click="true"
            aria-label="View resume"
            onClick={(e) => {
              e.stopPropagation();
              window.open(row.resume_url, '_blank', 'noopener,noreferrer');
            }}
            className="inline-flex items-center gap-[5px] text-[12.5px] font-[500] text-primary px-2 py-1 rounded-[6px] whitespace-nowrap hover:bg-brand-bg-subtle transition-colors"
          >
            <FileText className="w-[14px] h-[14px]" strokeWidth={1.8} />
            Resume
          </button>
        ) : (
          <span className="text-[12.5px] text-muted-foreground">—</span>
        ),
    };

    if (currentStatus === 'COMPLETED') base.push(extractionCol);
    if (currentStatus !== 'COMPLETED' && currentStatus !== 'STARTED') {
      base.push({ ...remarksCol, width: '13%' });
    }
    if (currentStatus !== 'STARTED') base.push(resumeCol);

    return base;
  }, [currentStatus]);

  const emptyStateNode = (
    <div className="flex flex-col items-center gap-[10px] py-12 px-4 text-center">
      {searchQuery ? (
        <Search className="w-[26px] h-[26px] text-border" strokeWidth={1.6} />
      ) : (
        <Inbox className="w-[26px] h-[26px] text-border" strokeWidth={1.6} />
      )}
      <span className="text-[13.5px] font-semibold text-muted-foreground">
        {searchQuery ? 'No candidates found' : 'No candidates in this status'}
      </span>
      <span className="text-[12.5px] text-muted-foreground">
        {searchQuery
          ? 'Try adjusting your search.'
          : 'Try another status or adjust your search.'}
      </span>
    </div>
  );

  const totalItems = serverPagination?.total ?? candidates.length;
  const totalPages =
    serverPagination?.pages ??
    Math.max(1, Math.ceil(totalItems / currentPageSize));

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Upload Tracker', href: '/upload-tracker' },
    { label: jobName, href: `/upload-tracker/${jobRef}` },
  ];

  return (
    <div>
      <PageHeader breadcrumbs={breadcrumbs} />

      <PageContainer>
        {/* Page header */}
        <header className="mb-5">
          <h1 className="text-[22px] font-bold tracking-[-0.04em] text-foreground-strong m-0 mb-2">
            {jobName}
          </h1>
          <div className="flex items-center gap-[10px] flex-wrap text-[12px] text-muted-foreground">
            {jobRef && (
              <span className="font-mono text-[11.5px] font-semibold tracking-[0.02em] text-primary bg-brand-bg-subtle px-2 py-[3px] rounded-[4px] border border-[rgba(32,104,178,0.12)]">
                {jobRef}
              </span>
            )}
            {Object.entries(sourceTotals)
              .filter(([, count]) => count > 0)
              .map(([src]) => (
                <React.Fragment key={src}>
                  <MetaSep />
                  <PlatPill source={src} />
                </React.Fragment>
              ))}
            {totalCandidates > 0 && (
              <>
                <MetaSep />
                <span>
                  <strong className="text-foreground-strong font-bold tabular-nums text-gray-400 dark:text-gray-100">
                    {totalCandidates}
                  </strong>{' '}
                  Uploaded
                </span>
              </>
            )}
          </div>
        </header>

        {/* Status tabs */}
        <div
          className="flex items-center border-b border-borderColorSecondary mb-4"
          role="tablist"
          aria-label="Filter by status"
        >
          {STATUS_META.filter(
            (s) =>
              !(
                s.key === 'STARTED' &&
                dataLoaded &&
                (statusCounts.STARTED ?? 0) === 0
              )
          ).map((s) => (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={currentStatus === s.key}
              onClick={() => handleStatusChange(s.key)}
              className={cn(
                TAB_BASE,
                currentStatus === s.key ? TAB_ACTIVE : TAB_INACTIVE
              )}
            >
              <span
                className={cn(
                  'w-[7px] h-[7px] rounded-full shrink-0',
                  DOT_COLORS[s.key]
                )}
              />
              {s.label}
              <span
                className={cn(
                  'text-[11px] font-semibold px-[7px] py-[1px] rounded-[10px] tabular-nums',
                  currentStatus === s.key
                    ? 'bg-brand-bg-subtle text-primary'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {statusCounts[s.key] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* Filter bar — date range + search */}
        <div className="flex items-center gap-[10px] mb-4">
          <div className="inline-flex items-center gap-1 p-1 rounded-lg border border-input bg-card">
            {PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                onClick={() => handlePreset(preset)}
                className={cn(
                  SEG_BASE,
                  mode === preset.key ? SEG_ACTIVE : SEG_INACTIVE
                )}
              >
                {preset.label}
              </button>
            ))}
            <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    SEG_BASE,
                    mode === 'custom' ? SEG_ACTIVE : SEG_INACTIVE
                  )}
                >
                  <CalendarIcon className="w-3.5 h-3.5" strokeWidth={1.8} />
                  {customLabel}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0">
                <Calendar
                  mode="range"
                  selected={range}
                  onSelect={handleRangeSelect}
                  numberOfMonths={2}
                  defaultMonth={range?.from}
                  disabled={{ after: new Date() }}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex-1" />
          <div className="relative">
            <Search
              className="absolute left-[11px] top-1/2 -translate-y-1/2 w-[14px] h-[14px] text-muted-foreground pointer-events-none"
              strokeWidth={1.8}
            />
            <input
              type="text"
              placeholder="Search candidates…"
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-[34px] pr-3 py-2 text-[12.5px] text-foreground-strong border border-input rounded-lg w-[260px] outline-none bg-card placeholder:text-muted-foreground focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] transition-[border-color,box-shadow]"
            />
          </div>
        </div>

        {/* Candidate table */}
        <DataTable
          className="border-border shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
          data={candidates}
          columns={columns}
          loading={isLoading}
          emptyState={emptyStateNode}
          headerWhenEmpty
          onRowClick={(row) => setPreviewCandidate(row)}
        />
        {totalItems > 0 && (
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={currentPageSize}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="candidates"
          />
        )}
      </PageContainer>

      <CandidatePreviewModal
        open={!!previewCandidate}
        onOpenChange={(o) => {
          if (!o) setPreviewCandidate(null);
        }}
        phone={previewCandidate?.phone}
        email={previewCandidate?.email}
        fallbackName={previewCandidate?.candidate_name}
        resumeUrl={previewCandidate?.resume_url}
        onViewProfile={(id) =>
          navigate(`/candidates/${id}`, {
            state: {
              from: 'upload-tracker',
              jobRef,
              jobName,
              trackerQuery: searchParams.toString(),
              dateMode: mode,
              dateFrom: fromStr,
              dateTo: toStr,
            },
          })
        }
      />

      <Dialog
        open={!!errorDialog}
        onOpenChange={(o) => !o && setErrorDialog(null)}
      >
        <DialogContent className="max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-[8px]">
              <AlertCircle
                className="w-[15px] h-[15px] text-ds-danger shrink-0"
                strokeWidth={2}
              />
              Extraction Error
            </DialogTitle>
            {errorDialog?.name && (
              <DialogDescription>{errorDialog.name}</DialogDescription>
            )}
          </DialogHeader>
          <div className="rounded-lg bg-muted border border-border p-4">
            <p className="text-[12.5px] text-muted-foreground font-mono whitespace-pre-wrap break-all leading-[1.7] m-0">
              {errorDialog?.error}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetail;
